import { useState } from 'react'
import { eq, useLiveQuery } from '@tanstack/react-db'

import type {
  CalloutLogWorkNotePatch,
  CalloutLogWorkPhotoNoteInput,
  CalloutLogWorkScreenProps,
} from '@/screens/CalloutLogWorkScreen'
import { getAllowedCalloutStatusTransitions } from '@/db/domain/calloutStatus'
import type { User as UserModel } from '@/db/domain/models'
import { useDb } from '@/lib/dbContext'
import { getCurrentAssignment } from '@/lib/jobAssignments'
import { buildServiceFeed } from '@/screens/serviceFeed'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import type { Callout, Customer, Note, ServiceAssignment, StatusChangeLog } from '@/lib/schemas/db'
import { useAuth } from '@/lib/auth'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import { Sentry, withSpan } from '@/lib/sentry'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

type CalloutLogWorkStatus =
  | 'New'
  | 'Booked'
  | 'LeftSite'
  | 'Arrived'
  | 'Review'
  | 'ReadyToInvoice'
  | 'Invoiced'
  | 'Cancelled'
  // Blocked is reachable from anywhere (see withUniversalTransitions) —
  // dropping it here rendered a Blocked callout as "New" on Log Work.
  | 'Blocked'

type LogWorkState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | {
      kind: 'ready'
      screenProps: CalloutLogWorkScreenProps
      photoNoteError: string | null
      photoNoteRollbackId: string | null
      clearPhotoNoteError: () => void
    }

function mapCalloutStatus(status: Callout['status']): CalloutLogWorkStatus {
  switch (status) {
    case 'New':
    case 'Booked':
    case 'LeftSite':
    case 'Arrived':
    case 'Review':
    case 'ReadyToInvoice':
    case 'Invoiced':
    case 'Cancelled':
    case 'Blocked':
      return status
    default:
      return 'New'
  }
}

export function useCalloutLogWork(serviceIdentity: string): LogWorkState {
  const db = useDb()
  const { user } = useAuth()
  const [photoNoteError, setPhotoNoteError] = useState<string | null>(null)
  const [photoNoteRollbackId, setPhotoNoteRollbackId] = useState<string | null>(null)
  const tryPublicId = looksLikePublicId(serviceIdentity)

  const calloutByIdResult = useLiveQuery(
    (q) =>
      serviceIdentity
        ? q
            .from({ callouts: db.calloutsCollection })
            .where(({ callouts }) => eq(callouts.id, serviceIdentity))
            .findOne()
        : null,
    [serviceIdentity],
  )

  const calloutByPublicIdResult = useLiveQuery(
    (q) =>
      serviceIdentity && tryPublicId
        ? q
            .from({ callouts: db.calloutsCollection })
            .where(({ callouts }) => eq(callouts.publicId, serviceIdentity))
            .findOne()
        : null,
    [serviceIdentity, tryPublicId],
  )
  const callout =
    (calloutByIdResult.data as Callout | undefined) ??
    (calloutByPublicIdResult.data as Callout | undefined)
  const serviceId = callout?.id ?? serviceIdentity
  if (!tryPublicId && callout && (calloutByIdResult.data as Callout | undefined)) {
    captureLegacyIdRoute('callout', serviceIdentity)
  }

  const customerResult = useLiveQuery(
    (q) => {
      if (!callout?.customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, callout.customerId))
        .findOne()
    },
    [callout?.customerId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const notesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ notes: db.notesCollection })
            .where(({ notes }) => eq(notes.serviceId, serviceId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [serviceId],
  )
  const statusChangesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ statusChanges: db.statusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, serviceId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [serviceId],
  )
  const assignmentsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ assignments: db.calloutAssignmentsCollection })
            .where(({ assignments }) => eq(assignments.serviceId, serviceId))
        : null,
    [serviceId],
  )

  // Archived (Invoiced/Cancelled) callouts aren't in the live sync; pull one
  // in on demand so the locked Log Work tab still works.
  const recordQueriesSettled =
    !calloutByIdResult.isLoading &&
    !calloutByPublicIdResult.isLoading &&
    !calloutByIdResult.isError &&
    !calloutByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'callout',
    serviceIdentity,
    recordQueriesSettled && !callout,
  )

  if (
    hydrating ||
    calloutByIdResult.isLoading ||
    calloutByPublicIdResult.isLoading ||
    customerResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    assignmentsResult.isLoading
  ) return { kind: 'loading' }

  if (
    calloutByIdResult.isError ||
    calloutByPublicIdResult.isError ||
    customerResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError ||
    assignmentsResult.isError
  ) return { kind: 'error' }

  if (!callout) return { kind: 'not_found' }

  const linkedCustomer = customerResult.data as Customer | undefined
  const linkedCustomerName = linkedCustomer
    ? linkedCustomer.externalId
      ? `${linkedCustomer.fullName} – ${linkedCustomer.externalId}`
      : linkedCustomer.fullName
    : callout.contactName ?? ''

  const userList = ((usersResult.data ?? []) as UserModel[])
    .map((entry) => ({ id: entry.id, name: entry.name || entry.email }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const usersById = new Map(
    ((usersResult.data ?? []) as UserModel[]).map((entry) => [entry.id, entry]),
  )
  const currentAssignment = getCurrentAssignment(
    (assignmentsResult.data ?? []) as ServiceAssignment[],
    usersById,
  )

  const notes = buildServiceFeed(
    notesResult.data as Note[] | undefined,
    statusChangesResult.data as StatusChangeLog[] | undefined,
    db.getAuthorDisplayName,
  )

  const handleAddTextNote = (text: string) => {
    if (!text.trim()) return
    return Sentry.startSpan(
      { op: 'callout.note.add', name: 'Add text note', attributes: { serviceId: callout.id } },
      () => {
        const note = db.appendLog(callout.id, { text })
        return { id: note.id, createdAt: note.createdAt }
      },
    )
  }

  const handleAddPhotoNote = (input: CalloutLogWorkPhotoNoteInput) => {
    // The caption is optional — a photo with no words is still a note.
    const caption = input.caption.trim()
    setPhotoNoteError(null)
    setPhotoNoteRollbackId(null)

    const { log, transaction } = Sentry.startSpan(
      { op: 'callout.note.add_photo', name: 'Add photo note', attributes: { serviceId: callout.id } },
      () => db.appendLogWithTransaction(callout.id, {
        caption,
        photoUrl: input.photoUrls[0],
        photoUrls: input.photoUrls,
        photoFiles: input.photoFiles,
      }),
    )

    transaction?.isPersisted?.promise
      ?.then(() => setPhotoNoteError(null))
      .catch((error) => {
        setPhotoNoteError(
          formatPocketBaseError(error, {
            fallbackMessage: 'Unable to save photo note. Please try again.',
            fieldLabels: { photo: 'Photo' },
          }),
        )
        setPhotoNoteRollbackId(log.id)
        try {
          // Hard delete: the upload failed, so this note never became part
          // of the record and should leave no tombstone.
          if (db.notesCollection.has?.(log.id)) db.discardLog(callout.id, log.id)
        } catch {}
      })

    return { id: log.id, createdAt: log.createdAt }
  }

  const handleEditNote = (id: string, patch: CalloutLogWorkNotePatch) => {
    db.updateLog(callout.id, id, patch)
  }

  const handleDeleteNote = (id: string) => {
    db.deleteLog(callout.id, id)
  }

  const handleChangeStatus = async (
    nextStatus: Callout['status'],
    reason?: string,
    occurredAt?: string,
  ) =>
    withSpan(
      { op: 'callout.status.change', name: 'Change callout status from log work', attributes: { serviceId: callout.id, nextStatus } },
      () =>
        db.updateCalloutAndWait(callout.id, {
          status: nextStatus,
          statusReason: reason?.trim() || undefined,
          statusOccurredAt: occurredAt,
        }),
    )

  const handleAssign = async (userId: string) =>
    withSpan(
      { op: 'callout.assign', name: 'Assign callout from log work', attributes: { serviceId: callout.id, userId } },
      () => db.assignCalloutAndWait(callout.id, userId),
    )

  const handleCancelCallout = async (reason?: string) => {
    const normalizedReason = reason?.trim()
    if (!normalizedReason) throw new Error('Reason is required when cancelling a callout')
    await withSpan(
      { op: 'callout.status.change', name: 'Cancel callout', attributes: { serviceId: callout.id, nextStatus: 'Cancelled' } },
      () => db.updateCalloutAndWait(callout.id, { status: 'Cancelled', statusReason: normalizedReason }),
    )
  }

  return {
    kind: 'ready',
    photoNoteError,
    photoNoteRollbackId,
    clearPhotoNoteError: () => {
      setPhotoNoteError(null)
      setPhotoNoteRollbackId(null)
    },
    screenProps: {
      id: callout.id,
      publicId: callout.publicId,
      resourceLabel: 'Callout',
      invoiceRoute: 'callout',
      customerName: linkedCustomerName,
      customerId: callout.customerId,
      customerPublicId: linkedCustomer?.publicId,
      issueSummary: callout.issueSummary ?? '',
      status: mapCalloutStatus(callout.status),
      scheduledAt: callout.isWholeDay
        ? `${formatRelativeDateTime(callout.startTime, { includeTime: false })} · No Time`
        : formatRelativeDateTime(callout.startTime),
      address: callout.contactAddress ?? '',
      contactName: callout.contactName ?? '',
      contactPhone: callout.contactPhone ?? '',
      billables: [],
      notes,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      todos: [],
      newNoteAuthorName: user?.name ?? '',
      onAddTextNote: handleAddTextNote,
      onAddPhotoNote: handleAddPhotoNote,
      photoNoteError,
      photoNoteRollbackId,
      onClearPhotoNoteError: () => {
        setPhotoNoteError(null)
        setPhotoNoteRollbackId(null)
      },
      onEditNote: handleEditNote,
      onDeleteNote: handleDeleteNote,
      availableStatusTransitions: getAllowedCalloutStatusTransitions(callout.status),
      // Forward every argument. A wrapper that forgets a trailing optional
      // param still typechecks, which silently dropped occurredAt once — if you
      // add an argument to handleChangeStatus, add it here too.
      onChangeStatus: (status, reason, occurredAt) =>
        handleChangeStatus(status as Callout['status'], reason, occurredAt),
      users: userList,
      assignedUserId: currentAssignment?.assignment.userId,
      assignedUserName: currentAssignment
        ? currentAssignment.user?.name || currentAssignment.user?.email
        : undefined,
      onAssign: handleAssign,
      onCancelCallout: handleCancelCallout,
    },
  }
}
